import Helmet from 'react-helmet';
import React from 'react';
import { Category } from 'module/Model/Categories';
import { ContentItems } from 'module/View/Content';
import { createPureComponent, Mapper, PathUtils, TitleUtils, nn } from 'module/Common';
import { Localized } from 'module/Model/Localized';
import { NextPosts } from '../NextPosts';
import { Post } from 'module/Model/Post';
import { PostBigTitle } from './PostBigTitle';
import { PublicationDescription } from 'module/View/Publication/PublicationDescription';
import { PublicationScroll } from 'module/View/Publication/PublicationScroll';
import { Sizes, WindowSizeConsumer, WishHandler, MountedStateProvider } from 'module/View/Common';
import { Wish } from 'module/Model/Wish';
import { Page } from 'module/Model/Page';
import { Publication } from 'module/Model/Publication';
import posed from 'react-pose';

const FadeInOut = posed.div({
  init: { opacity: 1 },
  normal: { opacity: 1 },
  exit: { opacity: 0 },
});

const PublicationHandler = WishHandler.create<Post | Page>();

type Ref = (el: HTMLElement) => void;

type Props = {
  adminBar: boolean;
  isPage: boolean;
  availableWidth: number;
  previewData?: any;
  publicationWish: Wish<Post | Page>;
  categoriesWish: false | Wish<Array<Category>>;
  lang: PathUtils.Lang;
  routeIsInExitState: boolean;
};

export const PublicationView = createPureComponent<Props>(
  'PublicationView',
  ({ publicationWish, categoriesWish, lang, previewData, adminBar, availableWidth, routeIsInExitState, isPage }) => {
    const publiMapper = isPage ? Page.mapper : Post.mapper;
    const data: Wish<Post | Page> = previewData
      ? Wish.createResolved(Mapper.exec(publiMapper as any, previewData))
      : publicationWish;
    const cols = Sizes.getContentColums(availableWidth);
    return (
      <WindowSizeConsumer>
        {windowSize => (
          <PublicationHandler
            wish={data}
            handleResolved={publication => {
              const post: Post | null = isPage ? null : (publication as any);
              const isPost = !isPage;

              const title = Publication.getTitle(publication);

              return (
                <>
                  <Helmet>
                    <title>{TitleUtils.create(Localized.get(title, lang))}</title>
                  </Helmet>
                  <MountedStateProvider delay={1000}>
                    {mounted => {
                      const state = routeIsInExitState ? 'exit' : mounted ? 'normal' : 'init';
                      const descriptionEnabled = Publication.isDescriptionEnabled(publication);

                      const renderDescription = (inline: boolean, innerRef: (Ref) | null) => {
                        return (
                          <PublicationDescription
                            title={Publication.getTitle(publication)}
                            description={Publication.getDescription(publication)}
                            cols={cols}
                            categoriesWish={categoriesWish}
                            inline={inline}
                            lang={lang}
                            innerRef={innerRef || undefined}
                          />
                        );
                      };

                      const renderHeaderAndContent = (headerRef: Ref | null, contentRef: Ref | null) => {
                        return (
                          <>
                            {descriptionEnabled && (
                              <ContentItems
                                items={Publication.getHeaderContent(publication)}
                                isCover={true}
                                cols={cols}
                                availableWidth={availableWidth}
                                innerRef={headerRef || undefined}
                                zIndex={0}
                                coverviewColor={isPost ? nn(post).overviewColor : null}
                              />
                            )}
                            <ContentItems
                              items={Publication.getBodyContent(publication)}
                              cols={cols}
                              zIndex={300}
                              availableWidth={availableWidth}
                              innerRef={contentRef || undefined}
                              coverviewColor={isPost ? nn(post).overviewColor : null}
                            />
                          </>
                        );
                      };

                      if (cols.nbOfColumns <= 3) {
                        return (
                          <FadeInOut pose={state}>
                            {descriptionEnabled && renderDescription(true, null)}
                            {renderHeaderAndContent(null, null)}
                          </FadeInOut>
                        );
                      }
                      return (
                        <PublicationScroll
                          ready={mounted}
                          cols={cols}
                          windowWidth={windowSize.width}
                          titleLinesCount={isPage ? 2 : nn(post).titleLines.length}
                          availableWidth={availableWidth}
                          adminBar={adminBar}
                          additionTopMargin={
                            isPage
                              ? 0
                              : nn(post).showTitleLines
                                ? Sizes.getContentAdditionalTopPadding(windowSize.width)
                                : 0
                          }
                        >
                          {({ descriptionRef, contentRef, headerRef, titleRefs }) => {
                            return (
                              <FadeInOut pose={state}>
                                {isPost &&
                                  nn(post).showTitleLines && (
                                    <div style={{ height: Sizes.getContentAdditionalTopPadding(windowSize.width) }} />
                                  )}
                                {isPost &&
                                  nn(post).showTitleLines && <PostBigTitle post={nn(post)} linesRefs={titleRefs} />}
                                {descriptionEnabled && renderDescription(false, descriptionRef)}
                                {renderHeaderAndContent(headerRef, contentRef)}
                              </FadeInOut>
                            );
                          }}
                        </PublicationScroll>
                      );
                    }}
                  </MountedStateProvider>
                  {isPost && <NextPosts availableWidth={availableWidth} />}
                </>
              );
            }}
          />
        )}
      </WindowSizeConsumer>
    );
  }
);



// WEBPACK FOOTER //
// ./src/module/View/Publication/PublicationView.tsx